import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { NexsetSelector, SelectedNexset } from '../AIToolsSection/NexsetSelector';
import { extractApiError } from '../../utils/errorUtils';
import { DetailView } from 'new/elements/DetailView';
import { useSelector } from 'react-redux';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { loadDataSet, fetchDatasetDoc } from 'js/actions';

interface CreateToolFromNexsetDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { nexset_id: number }) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export const CreateToolFromNexsetDialog: React.FC<CreateToolFromNexsetDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const [selectedNexsets, setSelectedNexsets] = useState<SelectedNexset[]>([]);
  const [previewNexsetId, setPreviewNexsetId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const previewNexset = useSelector((state: any) =>
    previewNexsetId ? state.entities?.dataSets?.[previewNexsetId] : null,
  );

  const handleSelectionChange = useCallback((nexsets: SelectedNexset[]) => {
    setSelectedNexsets(nexsets);
  }, []);

  const handlePreview = useCallback(
    (nexset: SelectedNexset) => {
      setPreviewNexsetId(nexset.id);
      dispatch(loadDataSet(nexset.id, { aclQueryMode: 'all' }) as any);
      dispatch(fetchDatasetDoc(nexset.id) as any);
    },
    [dispatch],
  );

  const handleClose = () => {
    if (isLoading) return;
    setSelectedNexsets([]);
    setPreviewNexsetId(null);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedNexsets.length === 0) return;
    setSubmitError(null);

    try {
      await onSubmit({
        nexset_id: parseInt(selectedNexsets[0].id, 10),
      });
      handleClose();
    } catch (err) {
      setSubmitError(extractApiError(err, 'Failed to create tool'));
    }
  };

  const displayError = error || submitError;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: 700 } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Create Tool from Nexset</Typography>
          <IconButton size="small" onClick={handleClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {displayError && (
            <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setSubmitError(null)}>
              {displayError}
            </Alert>
          )}

          <Box sx={{ flex: 1, p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a Nexset to create an AI tool from. The tool will be generated automatically
              from the Nexset's schema.
            </Typography>
            <NexsetSelector
              selectedNexsets={selectedNexsets}
              onSelectionChange={handleSelectionChange}
              onPreview={handlePreview}
              multiSelect={false}
              height={380}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || selectedNexsets.length === 0}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isLoading ? 'Creating...' : 'Create Tool'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nexset Preview Detail View */}
      {previewNexsetId && (
        <DetailView
          data={previewNexset ? { ...previewNexset, type: 'DATA_SET' } : undefined}
          onClose={() => setPreviewNexsetId(null)}
          zIndex={1400}
        />
      )}
    </>
  );
};
